/**
 * The way into, and out of, a support session (DOP-562).
 *
 *   /support/enter#code=…  the internal support console sends an operator here
 *                          with a one-time code; it is traded for the session
 *                          token and the app reloads as the customer user.
 *   /support/ended         where the tab lands on Exit, expiry or revocation.
 *
 * The code rides in the URL FRAGMENT so it never reaches a server log, and it
 * is removed from the address bar before anything else happens.
 */

import React, { useEffect, useState } from "react";
import { exchangeSupportCode } from "../services/supportApi";
import {
	clearSupportToken,
	rememberConsoleUrl,
	rememberedConsoleUrl,
	setSupportToken,
} from "../utils/sessionToken";

// The code is single-use and StrictMode runs effects twice in development: the
// second exchange would burn the session the first one opened.
let exchangeStarted = false;

const Shell: React.FC<{ title: string; children?: React.ReactNode }> = ({
	title,
	children,
}) => (
	<div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
		<div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm">
			<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-700">
				Doppler support
			</div>
			<h1 className="mb-3 text-lg font-semibold text-neutral-900">{title}</h1>
			<div className="text-sm text-neutral-600">{children}</div>
		</div>
	</div>
);

const ConsoleLink: React.FC = () => {
	const url = rememberedConsoleUrl();
	if (!url) return <p>You can close this tab.</p>;
	return (
		<a
			href={url}
			className="mt-2 inline-block rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700"
		>
			Back to the support console
		</a>
	);
};

export const SupportEnter: React.FC = () => {
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (exchangeStarted) return;
		exchangeStarted = true;

		const code = new URLSearchParams(window.location.hash.slice(1)).get("code");
		window.history.replaceState({}, "", window.location.pathname);
		if (!code) {
			setError("This link carries no support code.");
			return;
		}

		// A previous session in this tab must not outlive the new link, whether
		// or not the new code turns out to be good.
		clearSupportToken();
		exchangeSupportCode(code)
			.then(({ access_token, session }) => {
				setSupportToken(access_token);
				rememberConsoleUrl(session.console_url);
				// A full load, so every provider starts from the session's token
				// rather than from whatever this tab was before.
				window.location.replace("/");
			})
			.catch((e: unknown) => {
				setError(
					e instanceof Error
						? e.message
						: "This support link could not be opened.",
				);
			});
	}, []);

	if (error) {
		return (
			<Shell title="This support link did not open">
				<p className="mb-3">{error}</p>
				<p className="mb-3">
					Links work once and for 60 seconds. Start again from the console.
				</p>
				<ConsoleLink />
			</Shell>
		);
	}
	return <Shell title="Opening support session…" />;
};

export const SupportEnded: React.FC = () => {
	useEffect(() => {
		clearSupportToken();
	}, []);

	return (
		<Shell title="Support session ended">
			<p className="mb-3">
				This tab is no longer signed in as the customer. The session and
				everything done in it are on the audit record.
			</p>
			<ConsoleLink />
		</Shell>
	);
};
